import { TabsContent } from '@/components/ui/tabs';
import { VerticalTabs, VerticalTabsList, VerticalTabsTrigger } from '@/components/ui/vertical-tabs';
import { Logo } from '@/components/logos/logo';
import React from 'react';

interface TabConfig {
  label: string;
  value: string;
  disabled: boolean;
  setupCompleted: boolean;
  isPublishable?: boolean;
}

// Define a type for the content items
interface ContentItem {
  key: string;
  content: React.ReactNode;
}

export const createSetupNavigator = (
  getDisplayTabs: () => TabConfig[],
  getActiveTab: () => string,
  setDisplayTabsState: (newTabs: TabConfig[]) => void,
  performTabChangeNavigation: (newTabValue: string) => void
) => {
  return () => {
    const currentTabs = getDisplayTabs();
    const currentActiveTab = getActiveTab();
    let newTabs = [...currentTabs];

    const currentTabIndex = newTabs.findIndex((t) => t.value === currentActiveTab);

    if (currentTabIndex !== -1) {
      newTabs[currentTabIndex] = { ...newTabs[currentTabIndex], setupCompleted: true };
    }

    const nextIncompleteTabIndex = newTabs.findIndex((t) => !t.setupCompleted);

    if (nextIncompleteTabIndex !== -1) {
      newTabs = newTabs.map((tab, index) =>
        index === nextIncompleteTabIndex ? { ...tab, disabled: false, setupCompleted: false } : tab
      );
      const nextTabValue = newTabs[nextIncompleteTabIndex].value;
      if (newTabs[nextIncompleteTabIndex].isPublishable ?? true) {
        performTabChangeNavigation(nextTabValue); // This will handle setActiveTab and navigate
      }
    }
    // Always update the displayTabs state
    setDisplayTabsState(newTabs);
  };
};

interface DestinationTrackingDetailsProps {
  displayTabs: TabConfig[];
  isSetup: boolean;
  activeTab: string;
  onTabChange: (newTabValue: string) => void;
  id: string; // Assuming id is always a string and required for navigation logic within onTabChange
  content: ContentItem[]; // Use the more specific type here
}

const renderTabIcon = (tab: TabConfig, activeTab: string, isSetup: boolean, index: number) => {
  if (!isSetup) {
    return null;
  }

  if (tab.setupCompleted) {
    return <Logo icon="circleCheck" className="mr-2" width={16} height={16} />;
  }
  if (index === 0) {
    return <Logo icon="circleDotDashed" className="mr-2" width={16} height={16} />;
  }
  if (tab.disabled) {
    return <Logo icon="circleDashed" className="mr-2" width={16} height={16} />;
  }
  if (activeTab === tab.value) {
    return <Logo icon="circleHalf" className="mr-2" width={16} height={16} />;
  }
  return <Logo icon="circleDashed" className="mr-2" width={16} height={16} />;
};

export const DestinationTrackingDetails: React.FC<DestinationTrackingDetailsProps> = ({
  displayTabs,
  isSetup,
  activeTab,
  onTabChange,
  id,
  content
}) => {
  return (
    <VerticalTabs
      value={activeTab}
      onValueChange={onTabChange}
      id={id}
      className="flex gap-6 md:flex-row flex-col overflow-y-hidden"
    >
      <VerticalTabsList>
        {displayTabs.map((tab, index) => (
          <VerticalTabsTrigger
            disabled={isSetup && (tab.disabled || !(tab.isPublishable ?? true))}
            value={tab.value}
            key={tab.value}
          >
            {renderTabIcon(tab, activeTab, isSetup, index)}
            {tab.label}
          </VerticalTabsTrigger>
        ))}
      </VerticalTabsList>
      {content.map((c) => (
        <TabsContent value={c.key} key={c.key} className="mt-0 pt-0">
          {c.content}
        </TabsContent>
      ))}
    </VerticalTabs>
  );
};
